import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

/** Keep at least this much of the header on-screen (matches DraggableModal). */
const HEADER_VISIBLE_BUFFER = 40;

interface Offset { x: number; y: number; }

const ORIGIN: Offset = { x: 0, y: 0 };

/** The exact string `panelStyle` would render for this offset ('' at origin). */
const transformFor = (offset: Offset) =>
  offset.x === 0 && offset.y === 0 ? '' : `translate(${offset.x}px, ${offset.y}px)`;

/** Write the offset straight to the DOM, bypassing React. */
const applyTransform = (panel: HTMLElement | null, offset: Offset) => {
  if (panel) panel.style.transform = transformFor(offset);
};

export interface ModalDragHandleProps {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
}

export interface UseModalDragResult {
  panelRef: React.RefObject<HTMLDivElement | null>;
  /** Spread onto the panel element (the box that should move). */
  panelStyle: React.CSSProperties;
  /** Spread onto the header element (the drag handle). */
  dragHandleProps: ModalDragHandleProps;
}

/**
 * Drag-to-move for a modal panel that is centered by its overlay (flex or
 * inline-block centering). Applies a translate() delta on top of the panel's
 * static position, clamped so at least HEADER_VISIBLE_BUFFER px of the header
 * row stays inside the viewport. The offset resets when `active` flips false,
 * so every open starts centered.
 *
 * The panel must NOT have transition classes that animate `transform`, and it
 * must be centered by its container (flex/inline-block centering on the
 * overlay), NOT by its own translate classes (`-translate-x-1/2
 * -translate-y-1/2` etc.) — the hook's inline `transform` clobbers
 * class-based transforms.
 *
 * Perf: while a drag is in flight the offset is written straight to
 * `panelRef.current.style.transform` — no setState per `pointermove`, so the
 * host component (which for TanTable is an 8,000-line virtualized grid) does
 * not re-render at pointer frequency. React state is committed exactly once, on
 * pointerup/pointercancel, so `panelStyle` remains the source of truth between
 * drags. This is internal to the hook: the API and every consumer are
 * unchanged.
 */
export function useModalDrag(active: boolean): UseModalDragResult {
  const panelRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState<Offset>(ORIGIN);
  const offsetRef = useRef(offset);
  /**
   * The in-flight offset written imperatively but not yet committed to state.
   * Non-null only for the duration of a drag; while set, it outranks `offset`.
   */
  const liveOffsetRef = useRef<Offset | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startOffsetX: number;
    startOffsetY: number;
    /** Panel's undragged viewport position at drag start. */
    baseLeft: number;
    baseTop: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  // A render can land mid-drag for reasons unrelated to dragging (parent state,
  // or the panel element itself remounting). React would then paint the stale
  // committed `panelStyle` — or a bare style on a fresh node — and the panel
  // would snap back. Reassert the in-flight transform after every render so the
  // live drag always wins until it commits.
  useLayoutEffect(() => {
    if (liveOffsetRef.current) applyTransform(panelRef.current, liveOffsetRef.current);
  });

  useEffect(() => {
    if (!active) {
      liveOffsetRef.current = null;
      dragStateRef.current = null;
      // An uncommitted drag leaves a transform React does not know about; clear
      // it here or the next open would start off-centre.
      applyTransform(panelRef.current, ORIGIN);
      setOffset(ORIGIN);
    }
  }, [active]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const target = e.target as HTMLElement;
    if (
      target.closest('button, input, select, textarea, a, label, [role="button"], [contenteditable="true"]')
    ) {
      return;
    }
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const current = liveOffsetRef.current ?? offsetRef.current;
    dragStateRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startOffsetX: current.x,
      startOffsetY: current.y,
      baseLeft: rect.left - current.x,
      baseTop: rect.top - current.y,
      width: rect.width,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  /**
   * Hand the in-flight offset over to React state — the single setState of a
   * drag. Writes the DOM first so the rendered `panelStyle` and the inline
   * transform can never disagree, even for a frame.
   */
  const commitLiveOffset = useCallback(() => {
    const live = liveOffsetRef.current;
    liveOffsetRef.current = null;
    if (!live) return;
    const prev = offsetRef.current;
    applyTransform(panelRef.current, live);
    if (live.x !== prev.x || live.y !== prev.y) {
      offsetRef.current = live;
      setOffset(live);
    }
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    // A missed pointerup (e.g. modal closed mid-drag while the host stayed
    // mounted) must not let a buttonless mouse resume the drag.
    if (e.pointerType === 'mouse' && e.buttons === 0) {
      dragStateRef.current = null;
      // Nothing will deliver the pointerup, so bank whatever the abandoned drag
      // painted; otherwise the position would only live in the DOM.
      commitLiveOffset();
      return;
    }
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const rawX = drag.startOffsetX + (e.clientX - drag.startClientX);
    const rawY = drag.startOffsetY + (e.clientY - drag.startClientY);
    const minLeft = -(drag.width - HEADER_VISIBLE_BUFFER);
    const maxLeft = window.innerWidth - HEADER_VISIBLE_BUFFER;
    const minTop = 0;
    const maxTop = window.innerHeight - HEADER_VISIBLE_BUFFER;
    const next: Offset = {
      x: Math.min(Math.max(drag.baseLeft + rawX, minLeft), maxLeft) - drag.baseLeft,
      y: Math.min(Math.max(drag.baseTop + rawY, minTop), maxTop) - drag.baseTop,
    };
    // Hot path: paint via the DOM, not via a render.
    liveOffsetRef.current = next;
    applyTransform(panelRef.current, next);
  }, [commitLiveOffset]);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragStateRef.current = null;
    commitLiveOffset();
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, [commitLiveOffset]);

  const panelStyle: React.CSSProperties = useMemo(
    () =>
      offset.x === 0 && offset.y === 0
        ? {}
        : { transform: `translate(${offset.x}px, ${offset.y}px)` },
    [offset],
  );

  const dragHandleProps: ModalDragHandleProps = useMemo(
    () => ({ onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp }),
    [onPointerDown, onPointerMove, onPointerUp],
  );

  return { panelRef, panelStyle, dragHandleProps };
}

export default useModalDrag;
